import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiShoppingCart, 
  FiTrash2, 
  FiArrowLeft, 
  FiPlay,
  FiClock,
  FiUser,
  FiCheck
} from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Cart = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { items, total, removeFromCart, clearCart, debugCart } = useCart();

  // Debug cart state on component mount
  React.useEffect(() => {
    console.log('Cart component mounted with items:', items.length);
    console.log('Is authenticated:', isAuthenticated);
    debugCart();
  }, [debugCart, items.length, isAuthenticated]);

  // Debug items changes
  React.useEffect(() => {
    console.log('Cart items changed:', items);
    console.log('Cart total changed:', total);
  }, [items, total]);

  const handleRemoveItem = (courseId) => {
    console.log('Removing item from cart:', courseId);
    removeFromCart(courseId);
    toast.success('강의가 장바구니에서 제거되었습니다');
  };

  const handleClearCart = () => {
    clearCart();
    toast.success('장바구니가 비워졌습니다');
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnUrl: '/cart' } });
      return;
    }
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate('/courses');
  };

  // Show login prompt if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <FiArrowLeft className="w-5 h-5 mr-2" />
              뒤로 가기
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">장바구니</h1>
            <p className="text-gray-600">로그인하여 장바구니를 확인하세요</p>
          </div>

          {/* Login Required */}
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FiShoppingCart className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">로그인이 필요합니다</h2>
            <p className="text-gray-600 mb-8">장바구니를 확인하고 구매하려면 로그인해주세요</p>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/login', { state: { returnUrl: '/cart' } })}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                로그인하기
              </button>
              <button
                onClick={handleContinueShopping}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                강의 둘러보기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <FiArrowLeft className="w-5 h-5 mr-2" />
              뒤로 가기
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">장바구니</h1>
            <p className="text-gray-600">선택한 강의들을 확인하고 구매하세요</p>
          </div>

          {/* Empty Cart */}
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FiShoppingCart className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">장바구니가 비어있습니다</h2>
            <p className="text-gray-600 mb-8">관심 있는 강의를 장바구니에 추가해보세요</p>
            <button
              onClick={handleContinueShopping}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              강의 둘러보기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="w-5 h-5 mr-2" />
            뒤로 가기
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">장바구니</h1>
              <p className="text-gray-600">{items.length}개의 강의가 선택되었습니다</p>
            </div>
            <button
              onClick={handleClearCart}
              className="flex items-center px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiTrash2 className="w-4 h-4 mr-2" />
              장바구니 비우기
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">선택된 강의</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {items.map((course) => (
                  <div key={course._id} className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Course Thumbnail */}
                      <div className="flex-shrink-0">
                        <img
                          src={course.thumbnail || '/images/default-course.svg'}
                          alt={course.title}
                          className="w-24 h-16 object-cover rounded-lg"
                        />
                      </div>

                      {/* Course Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {course.title}
                            </h3>
                            <p className="text-gray-600 text-sm mb-2">
                              {course.subtitle}
                            </p>
                            
                            {/* Course Details */}
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                              <div className="flex items-center">
                                <FiUser className="w-4 h-4 mr-1" />
                                <span>{course.createdBy?.name || course.instructor?.name || 'Unknown'}</span>
                              </div>
                              <div className="flex items-center">
                                <FiClock className="w-4 h-4 mr-1" />
                                <span>{course.totalDuration || 0}시간</span>
                              </div>
                              <div className="flex items-center">
                                <FiPlay className="w-4 h-4 mr-1" />
                                <span>{course.totalLessons || 0}강의</span>
                              </div>
                            </div>

                            {/* Course Features */}
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <div className="flex items-center">
                                <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                                <span>평생 수강</span>
                              </div>
                              <div className="flex items-center">
                                <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                                <span>수료증</span>
                              </div>
                              <div className="flex items-center">
                                <FiCheck className="w-3 h-3 mr-1 text-green-500" />
                                <span>모바일 지원</span>
                              </div>
                            </div>
                          </div>

                          {/* Price and Remove */}
                          <div className="flex flex-col items-end space-y-2">
                            <div className="text-right">
                              <div className="text-xl font-bold text-gray-900">
                                ₩{(course.price || 0).toLocaleString()}
                              </div>
                              {course.originalPrice && course.price && course.originalPrice > course.price && (
                                <div className="text-sm text-gray-500 line-through">
                                  ₩{course.originalPrice.toLocaleString()}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveItem(course._id)}
                              className="flex items-center px-3 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4 mr-1" />
                              <span className="text-sm">제거</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">주문 요약</h2>
              
              {/* Summary Details */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">강의 수</span>
                  <span className="font-medium">{items.length}개</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">소계</span>
                  <span className="font-medium">₩{total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">할인</span>
                  <span className="font-medium text-green-600">₩0</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">총 금액</span>
                    <span className="text-lg font-bold text-gray-900">₩{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                <FiShoppingCart className="w-5 h-5 mr-2" />
                {isAuthenticated ? '결제 진행하기' : '로그인 후 결제'}
              </button>

              {/* Continue Shopping */}
              <button
                onClick={handleContinueShopping}
                className="w-full mt-3 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                더 많은 강의 둘러보기
              </button>

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">
                  <FiCheck className="w-4 h-4 mr-2 text-green-500" />
                  <span>안전한 결제 시스템</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <FiCheck className="w-4 h-4 mr-2 text-green-500" />
                  <span>30일 환불 보장</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <FiCheck className="w-4 h-4 mr-2 text-green-500" />
                  <span>평생 수강 가능</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 